/**
 * Настройки → Клиника (ТЗ §8.7): название, короткое название и короткий код для номеров дел.
 *
 * Короткий код входит в человекочитаемый номер `<код>-<год>-<порядковый>`; смена кода
 * не переписывает номера уже заведённых дел (`docs/spec/_api-contract.md`, раздел 8).
 */

import { useEffect, useState, type FormEvent } from 'react';
import type { ClinicDto, ClinicPatchRequest } from '@/api/types';
import { errorMessage, FormError, LoadingState } from '@/components/ErrorState';

export interface ClinicTabProps {
    clinic: ClinicDto | null;
    loading?: boolean;
    error?: unknown;
    onSave: (patch: ClinicPatchRequest) => Promise<unknown>;
}

export default function ClinicTab({ clinic, loading, error, onSave }: ClinicTabProps) {
    const [name, setName] = useState('');
    const [shortName, setShortName] = useState('');
    const [shortCode, setShortCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const clinicKey = clinic
        ? `${clinic.id}:${clinic.name}:${clinic.short_name ?? ''}:${clinic.short_code ?? ''}`
        : '';

    useEffect(() => {
        setName(clinic?.name ?? '');
        setShortName(clinic?.short_name ?? '');
        setShortCode(clinic?.short_code ?? '');
        setSaved(false);
        // clinicKey меняется только вместе с данными клиники.
    }, [clinicKey]);

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (busy || !clinic) return;

        const nextName = name.trim();
        const nextShortName = shortName.trim();
        const nextShortCode = shortCode.trim();

        if (!nextName) {
            setFormError('Название клиники обязательно.');
            return;
        }
        if (!nextShortCode) {
            setFormError('Короткий код нужен для номеров дел — без него номер не собрать.');
            return;
        }
        if (nextShortCode.length > 8) {
            setFormError('Короткий код длиннее восьми знаков сделает номер дела нечитаемым.');
            return;
        }

        setBusy(true);
        setFormError(null);
        void (async () => {
            try {
                await onSave({ name: nextName, short_name: nextShortName, short_code: nextShortCode });
                setSaved(true);
            } catch (cause) {
                setFormError(errorMessage(cause));
            } finally {
                setBusy(false);
            }
        })();
    };

    const sample = `${shortCode.trim() || 'КОД'}-${new Date().getFullYear()}-000123`;

    return (
        <div className="stat-block">
            <div className="block-header">
                <span>🏛️ Клиника</span>
                {clinic?.timezone ? (
                    <span className="checklist-progress">часовой пояс: {clinic.timezone}</span>
                ) : null}
            </div>
            <div className="block-body">
                <FormError>{error ? errorMessage(error) : null}</FormError>
                {loading ? <LoadingState variant="inline" label="Загружаем настройки клиники…" /> : null}
                {!loading && !clinic ? <div className="list-empty">Клиника не настроена.</div> : null}

                {clinic ? (
                    <form onSubmit={onSubmit}>
                        <div className="kv">
                            <span className="k">Полное название</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={48}
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    aria-label="Полное название клиники"
                                />
                            </span>

                            <span className="k">Короткое название</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={32}
                                    value={shortName}
                                    placeholder="Поликлиника № 3"
                                    onChange={(event) => setShortName(event.target.value)}
                                    aria-label="Короткое название клиники"
                                />
                            </span>

                            <span className="k">Код для номеров дел</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={10}
                                    maxLength={8}
                                    value={shortCode}
                                    placeholder="ГП3"
                                    onChange={(event) => setShortCode(event.target.value)}
                                    aria-label="Короткий код клиники"
                                />
                            </span>

                            <span className="k">Пример номера</span>
                            <span className="v">{sample}</span>
                        </div>

                        <FormError>{formError}</FormError>

                        <div className="draft-bar">
                            <button type="submit" className="act-btn primary" disabled={busy}>
                                {busy ? 'Сохраняем…' : 'Сохранить'}
                            </button>
                            <span className="draft-note">
                                {saved
                                    ? 'Настройки клиники сохранены.'
                                    : 'Смена кода не переписывает номера уже заведённых дел — новый код получат только новые дела.'}
                            </span>
                        </div>
                    </form>
                ) : null}
            </div>
        </div>
    );
}
